import React from 'react';
import { STYLES, COLORS } from './anchors';

// The look of each style button: the letter drawn the way the style marks text.
const STYLE_SAMPLE = {
  highlight: { backgroundColor: 'var(--anno-yellow-bg)' },
  underline: { textDecoration: 'underline', textDecorationThickness: '2px' },
  wavy:      { textDecoration: 'underline wavy', textDecorationThickness: '1.5px' },
};

const Icon = ({ d }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);
const COPY_ICON  = 'M9 9h10v12H9z M5 15H4V3h12v1';
const NOTE_ICON  = 'M4 20h4L19 9l-4-4L4 16v4z M13 7l4 4';
const LISTEN_ICON = 'M11 5L6 9H3v6h3l5 4V5z M15.5 8.5a5 5 0 0 1 0 7 M18.5 5.5a9 9 0 0 1 0 13';
const TRASH_ICON = 'M4 7h16 M9 7V4h6v3 M6 7l1 13h10l1-13';

// ── The floating toolbar over a selection or an existing mark ────────────────
// Presentational only; AnnotationLayer decides what each button does.
// `current` is the mark being edited ({ style, color, note }) or null for a
// fresh selection. `onListen` plays the narrated recording from the first
// selected verse ("listen from here"): the button only appears when the page
// passes a handler, which it does only where verse timings exist.
export const SelectionToolbar = React.forwardRef(function SelectionToolbar(
  { t, current, copied, onCopy, onNote, onStyle, onColor, onDelete, onListen, onOpenNote }, ref) {
  return (
    <div ref={ref} className="anno-toolbar" role="toolbar" aria-label={t.toolbar}
      // Keeps a mouse click on a button from clearing the selection it acts on.
      onMouseDown={e => { if (e.target.tagName !== 'TEXTAREA') e.preventDefault(); }}>
      {current?.note && (
        <button type="button" className="anno-note-preview" onClick={onOpenNote} title={t.openNote}>
          {current.note}
        </button>
      )}
      <div className="anno-toolbar-row">
        <button type="button" className="anno-tool" onClick={onCopy}>
          <Icon d={COPY_ICON} /><span>{copied ? t.copied : t.copy}</span>
        </button>
        <button type="button" className="anno-tool" onClick={onNote}>
          <Icon d={NOTE_ICON} /><span>{current?.note ? t.editNote : t.note}</span>
        </button>
        {onListen && (
          <button type="button" className="anno-tool" onClick={onListen} title={t.listenFrom}>
            <Icon d={LISTEN_ICON} /><span>{t.listen}</span>
          </button>
        )}
        {current && (
          <button type="button" className="anno-tool anno-tool-danger" onClick={onDelete}>
            <Icon d={TRASH_ICON} /><span>{t.remove}</span>
          </button>
        )}
      </div>
      <div className="anno-toolbar-row">
        {STYLES.filter(s => s !== 'none').map(s => (
          <button key={s} type="button" title={t[s]} aria-label={t[s]}
            aria-pressed={current?.style === s}
            className={`anno-style-btn${current?.style === s ? ' active' : ''}`}
            onClick={() => onStyle(s)}>
            <span style={STYLE_SAMPLE[s]}>A</span>
          </button>
        ))}
        <span className="anno-toolbar-sep" aria-hidden="true" />
        {COLORS.map(c => (
          <button key={c} type="button" title={t.colors[c]} aria-label={t.colors[c]}
            aria-pressed={current?.color === c}
            className={`anno-swatch${current?.color === c ? ' active' : ''}`}
            style={{ '--swatch': `var(--anno-${c}-line)` }}
            onClick={() => onColor(c)} />
        ))}
      </div>
    </div>
  );
});
